import {makeAutoObservable, runInAction} from "mobx";
import AdminManageMerchantWalletService, { IAdminMerchantWallet } from "../../../../services/api/admin-manage-merchant-wallet-service";
import {rootContainer, TYPE} from "../../../../services/container/root";
import {isSet} from "../../../../models/model-types-methods";
import CurrencyService from "../../../../services/currency-service";

export interface IWalletBalanceAmendmentRequest {
    amendmentReason?: string;
    amendmentAmount?: number | null;
}

class ManageMerchantWalletStore {
    private _model?: IAdminMerchantWallet;
    private _requestData: IWalletBalanceAmendmentRequest = {};
    private _shopId?: number;
    private _isLoading = false;
    private _isSaving = false;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    private get _merchantWalletService(): AdminManageMerchantWalletService {
        return rootContainer.get<AdminManageMerchantWalletService>(TYPE.AdminManageMerchantWalletService);
    }

    private get _currencyService(): CurrencyService {
        return rootContainer.get<CurrencyService>(TYPE.CurrencyService);
    }

    private _setModel(wallet: IAdminMerchantWallet | null): void {
        if (isSet(wallet)) {
            this._model = wallet;
        }
        this._requestData = {};
    }

    get isLoading(): boolean { return this._isLoading; }
    get isSaving(): boolean { return this._isSaving; }
    get hasWallet(): boolean { return isSet(this._model) && Object.keys(this._model as object).length > 0; }
    get amount(): number { return Number(this._model?.amount || null); }
    get currency(): string { return this._model?.currency || ''; }
    get currencySymbol(): string { return this._currencyService.getCurrencySymbol(this.currency); }
    get amendmentAmount(): number | null { return this._requestData.amendmentAmount || null; }
    setAmendmentAmount(newAmount: number | null) { this._requestData.amendmentAmount = newAmount; }
    get amendmentReason(): string { return this._requestData.amendmentReason || ''; }
    setAmendmentReason(reason: string) { this._requestData.amendmentReason = reason; }
    get isAmendmentDisabled(): boolean {
        return this.isSaving || !this.amendmentAmount || this.amendmentReason === ''
    }

    async load(shopId: number): Promise<void> {
        if(!isSet(shopId)) {
            return;
        }
        this._shopId = shopId;

        try {
            this._isLoading = true;
            const wallet = await this._merchantWalletService.fetchMerchantWallet(this._shopId);
            runInAction(() => {
                this._setModel(wallet);
            });
        }
        catch (e) {}
        finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    clearRequestData(): void {
        this._requestData = {};
    }

    async save(): Promise<void> {
        if (this.isAmendmentDisabled) {
            return;
        }
        if(!isSet(this._shopId)) {
            return;
        }

        this._isSaving = true;

        try {
            const wallet = await this._merchantWalletService.saveBalanceAmendment(this._shopId as number, this._requestData);

            runInAction(() => {
                this._setModel(wallet);
                this.clearRequestData();
            });
        }
        catch (e) {}
        finally {
            runInAction(() => { this._isSaving = false; })
        }
    }
}
export default new ManageMerchantWalletStore();